<template>
  <v-app id="inspire">
    <v-app-bar app color="dark" dark>
      <v-toolbar-title>
        Graph Layout Labelling
        <span class="text-caption ml-3 font-weight-regular" style="opacity: 0.75">Training</span>
      </v-toolbar-title>
    </v-app-bar>

    <v-main>
      <div v-if="questions.length > 0">
        <div class="text-center mb-1">
          <h2 class="text-h6">Please answer the following questions about this graph:</h2>
        </div>

        <!-- Graph Image Display (same layout as main study) -->
        <v-row class="mb-2">
          <v-col cols="12" class="text-center py-1">
            <v-card class="mx-auto study-banner">
              <v-img
                :src="graphImageUrl"
                contain
                height="100%"
                width="100%"
                class="grey lighten-2"
              >
                <template v-slot:placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
            </v-card>
          </v-col>
        </v-row>

        <!-- Questions Section (same button_choice UI as main study) -->
        <v-row>
          <v-col cols="12">
            <v-card class="mx-auto pa-3 study-answers">
              <p class="path-prompt text-center mb-1">
                What is the length of the shortest path between the two red/circled nodes?
              </p>
              <div class="button-choice-container">
                <v-btn-toggle
                  v-model="answer"
                  class="button-choice-group"
                  :disabled="submitted"
                  @change="onAnswerSelected"
                >
                  <v-btn
                    v-for="option in choiceOptions"
                    :key="option"
                    :value="option"
                    color="primary"
                    outlined
                    class="choice-button"
                  >
                    {{ option }}
                  </v-btn>
                  <v-btn
                    value="Not Sure"
                    color="primary"
                    outlined
                    class="choice-button not-sure-button"
                  >
                    Not Sure
                  </v-btn>
                </v-btn-toggle>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <v-container v-else>
        <v-alert type="warning">No training questions configured yet.</v-alert>
      </v-container>
    </v-main>

    <v-snackbar v-model="feedbackSnackbar" :color="feedbackColor" top timeout="1200">
      {{ feedbackMessage }}
    </v-snackbar>
  </v-app>
</template>

<script>
import prolificConfig from '@/config/prolific';
import config from '@/config';

export default {
  name: 'Training',
  title: 'Training',
  data() {
    return {
      sessionId: this.$route.params.sessionId,
      questions: [],
      currentIndex: 0,
      answer: null,
      submitted: false,
      submitting: false,
      passThreshold: prolificConfig.TRAINING_PASS_THRESHOLD,
      feedbackSnackbar: false,
      feedbackMessage: '',
      feedbackColor: 'success',
      startTime: null,
      choiceOptions: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'],
    };
  },
  computed: {
    currentQuestion() {
      return this.questions[this.currentIndex] || {};
    },
    graphImageUrl() {
      const url = this.currentQuestion.image_url;
      if (!url) return '';
      return url.startsWith('/') ? config.API_BASE_URL + url : url;
    },
  },
  created() {
    this.loadQuestions();
    this.loadConfig();
  },
  methods: {
    async loadConfig() {
      try {
        const resp = await fetch(`${config.API_BASE_URL}/study_config/`);
        const data = await resp.json();
        this.passThreshold = data.training_pass_threshold;
      } catch (e) {
        console.warn(e);
      }
    },
    async loadQuestions() {
      const resp = await fetch(`${config.API_BASE_URL}/prolific/training_questions/`);
      this.questions = await resp.json();
      this.startTime = Date.now();
    },
    onAnswerSelected() {
      if (this.submitted || this.submitting || !this.answer) return;
      // Match main study: short delay then submit
      setTimeout(() => {
        this.submitAnswer();
      }, 300);
    },
    async submitAnswer() {
      if (this.submitting || this.submitted || !this.answer) return;
      this.submitting = true;
      this.submitted = true;
      const seconds = ((Date.now() - this.startTime) / 1000).toFixed(2);
      try {
        const resp = await fetch(
          `${config.API_BASE_URL}/prolific/session/${this.sessionId}/training/submit/`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              training_question_id: this.currentQuestion.training_question_id,
              answer: this.answer,
              seconds_spent: seconds,
            }),
          }
        );
        const result = await resp.json();

        if (result.is_correct) {
          this.feedbackMessage = 'Correct!';
          this.feedbackColor = 'success';
        } else {
          this.feedbackMessage = 'Incorrect.';
          this.feedbackColor = 'error';
        }
        this.feedbackSnackbar = true;

        if (result.training_complete) {
          await this.finishTraining();
        } else {
          setTimeout(() => {
            this.currentIndex += 1;
            this.answer = null;
            this.submitted = false;
            this.submitting = false;
            this.startTime = Date.now();
          }, 1200);
        }
      } catch (e) {
        console.error(e);
        this.submitted = false;
        this.submitting = false;
      }
    },
    async finishTraining() {
      await fetch(
        `${config.API_BASE_URL}/prolific/session/${this.sessionId}/training/finish/`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      // Shared result page for pass / fail
      this.$router.push(`/prolific/study/${this.sessionId}/training-result`);
    },
  },
};
</script>

<style scoped>
.study-banner {
  height: 66vh !important;
  width: 87.45vh !important;
  min-width: unset !important;
  max-width: 100% !important;
  margin: 0 auto !important;
}
.study-answers {
  width: 100%;
  max-width: 760px;
}
.path-prompt {
  font-size: 21px;
  font-weight: 500;
  line-height: 1.25;
  white-space: nowrap;
}
.button-choice-container {
  text-align: center;
}

.button-choice-group {
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  gap: 6px;
  margin-bottom: 8px;
}

.choice-button {
  min-width: 48px !important;
  height: 48px !important;
  padding: 0 8px !important;
  font-size: 16px;
  font-weight: bold;
}

.choice-button.v-btn--active {
  background-color: #1976d2 !important;
  color: white !important;
}

.choice-button:hover {
  transform: scale(1.05);
  transition: transform 0.2s;
}

.not-sure-button {
  background-color: #f5f5f5 !important;
  color: #666 !important;
  border: 2px dashed #999 !important;
}

.not-sure-button.v-btn--active {
  background-color: #e3f2fd !important;
  color: #1976d2 !important;
  border: 2px solid #1976d2 !important;
}
</style>
